import { MouseEvent, useCallback, useEffect, useMemo, useState, useSyncExternalStore } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  List,
  Avatar,
  ListItemText,
  ListItemAvatar,
  Button,
  ListItemButton,
  ListItem,
  ListItemIcon,
  Checkbox,
  IconButton,
  Menu,
  MenuItem,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Typography,
} from "@mui/material";
import {
  Add,
  CalendarMonth,
  Call,
  Checklist,
  ContentCopy,
  DeleteOutline,
  Description,
  Email,
  MoreVert,
  OpenInNew,
  Sms,
} from "@mui/icons-material";
import { APICustomer, getAllCustomers } from "../api/customers";
import {
  getPending,
  scheduleDelete,
  subscribe,
  undoDelete,
  UNDO_MS,
} from "../utils/pendingDeletes";
import SplitPageView from "../components/SplitPageView";
import CustomerDetailsPage from "./CustomerDetailsPage";
import style from "./CustomerList.module.css";

const fullName = (c: Pick<APICustomer, "firstName" | "lastName">) =>
  `${c.firstName} ${c.lastName}`.trim() || "Unnamed customer";

type MenuState = { customer: APICustomer; x?: number; y?: number; anchor?: HTMLElement };

function CustomerListPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const id = searchParams.get("id");

  const [customersData, setCustomerData] = useState<APICustomer[]>([]);
  const [search, setSearch] = useState("");
  const [selecting, setSelecting] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [menu, setMenu] = useState<MenuState | null>(null);
  const [confirm, setConfirm] = useState<APICustomer[] | null>(null);
  const pending = useSyncExternalStore(subscribe, getPending);
  const [notice, setNotice] = useState("");

  const load = useCallback(
    () =>
      getAllCustomers().then((v) =>
        setCustomerData(
          [...v].sort((a, b) =>
            `${a.lastName} ${a.firstName}`.localeCompare(`${b.lastName} ${b.firstName}`)
          )
        )
      ),
    []
  );
  useEffect(() => {
    load();
  }, [id, load]);

  // When a waiting delete finishes, refresh the list.
  useEffect(() => subscribe(() => !getPending().length && load()), [load]);

  const undo = () => {
    const n = undoDelete();
    if (n) setNotice(`Restored ${n} customer${n === 1 ? "" : "s"}.`);
  };

  const startDelete = async (list: APICustomer[]) => {
    setConfirm(null);
    setSelected(new Set());
    setSelecting(false);
    if (list.some((c) => c.id === id)) navigate("/customers", { replace: true });
    await scheduleDelete(list);
  };

  const hidden = useMemo(() => new Set(pending.map((c) => c.id)), [pending]);
  const terms = search.toLowerCase().split(/\s+/).filter(Boolean);
  const visible = customersData.filter((c) => {
    if (hidden.has(c.id)) return false;
    const haystack = [c.firstName, c.lastName, c.phone, c.email, c.addressLine1, c.city]
      .join(" ")
      .toLowerCase();
    return terms.every((t) => haystack.includes(t));
  });

  const toggle = (cid: string) =>
    setSelected((s) => {
      const next = new Set(s);
      next.has(cid) ? next.delete(cid) : next.add(cid);
      return next;
    });
  const allVisibleSelected = visible.length > 0 && visible.every((c) => selected.has(c.id || ""));
  const selectAll = () =>
    setSelected(allVisibleSelected ? new Set() : new Set(visible.map((c) => c.id || "")));
  const exitSelect = () => {
    setSelecting(false);
    setSelected(new Set());
  };
  const selectedCustomers = customersData.filter((c) => selected.has(c.id || ""));

  // Keyboard: Esc leaves select mode, Delete asks to delete the selection.
  useEffect(() => {
    if (!selecting) return;
    const onKey = (e: KeyboardEvent) => {
      const typing = (e.target as HTMLElement)?.closest?.(
        "textarea, [contenteditable], input:not([type=checkbox]):not([type=radio])"
      );
      if (e.key === "Escape") exitSelect();
      else if ((e.key === "Delete" || e.key === "Backspace") && !typing && selected.size) {
        e.preventDefault();
        setConfirm(customersData.filter((c) => selected.has(c.id || "")));
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [selecting, selected, customersData]);

  const openMenu = (e: MouseEvent, customer: APICustomer, fromButton = false) => {
    e.preventDefault();
    e.stopPropagation();
    setMenu(
      fromButton
        ? { customer, anchor: e.currentTarget as HTMLElement }
        : { customer, x: e.clientX, y: e.clientY }
    );
  };
  const closeMenu = () => setMenu(null);
  const go = (to: string) => {
    closeMenu();
    navigate(to);
  };
  const copy = async (text: string, what: string) => {
    closeMenu();
    try {
      await navigator.clipboard.writeText(text);
      setNotice(`${what} copied.`);
    } catch {
      setNotice(`Couldn't copy the ${what.toLowerCase()}.`);
    }
  };
  const m = menu?.customer;

  return (
    <SplitPageView
      selected={!!searchParams.get("id") || !!searchParams.get("cid") || !!searchParams.get("new")}
      pageTitle="Customers"
      search={search}
      onSearchChange={setSearch}
      contentList={
        <>
          <List style={{ padding: 0 }}>
            {selecting ? (
              <ListItem className={style.toolbar}>
                <Checkbox
                  edge="start"
                  checked={allVisibleSelected}
                  indeterminate={selected.size > 0 && !allVisibleSelected}
                  onChange={selectAll}
                  inputProps={{ "aria-label": "Select all shown customers" }}
                />
                <Typography variant="body2" className={style.count}>
                  {selected.size} selected
                </Typography>
                <Button
                  color="error"
                  startIcon={<DeleteOutline />}
                  disabled={!selected.size}
                  onClick={() => setConfirm(selectedCustomers)}
                >
                  Delete
                </Button>
                <Button onClick={exitSelect}>Done</Button>
              </ListItem>
            ) : (
              <ListItem className={style.toolbar}>
                <Button
                  component={Link}
                  to="/customers?new=1"
                  variant="contained"
                  startIcon={<Add />}
                  className={style.grow}
                >
                  New Customer
                </Button>
                <Button
                  startIcon={<Checklist />}
                  onClick={() => setSelecting(true)}
                  disabled={!visible.length}
                >
                  Select
                </Button>
              </ListItem>
            )}
            {visible.map((customer) => {
              const cid = customer.id || "";
              const isSelected = selected.has(cid);
              return (
                <ListItem
                  key={cid}
                  disablePadding
                  onContextMenu={(e) => openMenu(e, customer)}
                  secondaryAction={
                    <IconButton
                      edge="end"
                      aria-label={`Actions for ${fullName(customer)}`}
                      onClick={(e) => openMenu(e, customer, true)}
                      className={style.more}
                    >
                      <MoreVert fontSize="small" />
                    </IconButton>
                  }
                >
                  <ListItemButton
                    component={selecting ? "div" : Link}
                    to={selecting ? undefined : `/customers?id=${cid}`}
                    selected={selecting ? isSelected : id === cid}
                    onClick={(e: MouseEvent) => {
                      // Cmd/Ctrl-click toggles the selection without opening.
                      if (selecting || e.metaKey || e.ctrlKey) {
                        e.preventDefault();
                        if (!selecting) setSelecting(true);
                        toggle(cid);
                      }
                    }}
                  >
                    {selecting && (
                      <ListItemIcon className={style.checkCell}>
                        <Checkbox
                          edge="start"
                          checked={isSelected}
                          tabIndex={-1}
                          disableRipple
                          inputProps={{ "aria-label": `Select ${fullName(customer)}` }}
                        />
                      </ListItemIcon>
                    )}
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: "primary.light" }}>
                        {customer.firstName && customer.firstName[0].toUpperCase()}
                        {customer.lastName && customer.lastName[0].toUpperCase()}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={fullName(customer)}
                      secondary={[customer.addressLine1, customer.city].filter(Boolean).join(", ")}
                      primaryTypographyProps={{ fontWeight: 600 }}
                    />
                  </ListItemButton>
                </ListItem>
              );
            })}
            {visible.length === 0 && (
              <ListItem>
                <ListItemText secondary={search ? "No customers match." : "No customers yet."} />
              </ListItem>
            )}
          </List>

          <Menu
            open={!!menu}
            onClose={closeMenu}
            anchorEl={menu?.anchor}
            anchorReference={menu?.anchor ? "anchorEl" : "anchorPosition"}
            anchorPosition={menu?.x !== undefined ? { top: menu.y || 0, left: menu.x } : undefined}
            onContextMenu={(e) => {
              e.preventDefault();
              closeMenu();
            }}
          >
            {m && [
              <MenuItem key="open" onClick={() => go(`/customers?id=${m.id}`)}>
                <ListItemIcon><OpenInNew fontSize="small" /></ListItemIcon>
                Open
              </MenuItem>,
              <MenuItem key="prop" onClick={() => go(`/proposals?cid=${m.id}`)}>
                <ListItemIcon><Description fontSize="small" /></ListItemIcon>
                New proposal
              </MenuItem>,
              <MenuItem key="appt" onClick={() => go(`/appointments?cid=${m.id}`)}>
                <ListItemIcon><CalendarMonth fontSize="small" /></ListItemIcon>
                Schedule
              </MenuItem>,
              <MenuItem key="email" onClick={() => go(`/emails?new=1&refType=customer&refId=${m.id}`)}>
                <ListItemIcon><Email fontSize="small" /></ListItemIcon>
                Email
              </MenuItem>,
              <MenuItem key="text" onClick={() => go(`/customers?id=${m.id}#texts`)} disabled={!m.phone}>
                <ListItemIcon><Sms fontSize="small" /></ListItemIcon>
                Text
              </MenuItem>,
              <MenuItem key="call" component="a" href={`tel:${m.phone}`} onClick={closeMenu} disabled={!m.phone}>
                <ListItemIcon><Call fontSize="small" /></ListItemIcon>
                Call
              </MenuItem>,
              <Divider key="d1" />,
              <MenuItem key="cphone" onClick={() => copy(m.phone, "Phone number")} disabled={!m.phone}>
                <ListItemIcon><ContentCopy fontSize="small" /></ListItemIcon>
                Copy phone
              </MenuItem>,
              <MenuItem key="cemail" onClick={() => copy(m.email, "Email address")} disabled={!m.email}>
                <ListItemIcon><ContentCopy fontSize="small" /></ListItemIcon>
                Copy email
              </MenuItem>,
              <Divider key="d2" />,
              <MenuItem
                key="select"
                onClick={() => {
                  closeMenu();
                  setSelecting(true);
                  toggle(m.id || "");
                }}
              >
                <ListItemIcon><Checklist fontSize="small" /></ListItemIcon>
                {selected.has(m.id || "") ? "Unselect" : "Select"}
              </MenuItem>,
              <MenuItem
                key="delete"
                onClick={() => {
                  closeMenu();
                  // Right-clicking one of several selected rows deletes the selection.
                  setConfirm(
                    selecting && selected.has(m.id || "") && selected.size > 1 ? selectedCustomers : [m]
                  );
                }}
                className={style.danger}
              >
                <ListItemIcon><DeleteOutline fontSize="small" color="error" /></ListItemIcon>
                {selecting && selected.has(m.id || "") && selected.size > 1
                  ? `Delete ${selected.size} selected…`
                  : "Delete…"}
              </MenuItem>,
            ]}
          </Menu>

          <Dialog open={!!confirm} onClose={() => setConfirm(null)} maxWidth="xs" fullWidth>
            <DialogTitle>
              Delete {confirm?.length === 1 ? fullName(confirm[0]) : `${confirm?.length} customers`}?
            </DialogTitle>
            <DialogContent>
              {confirm && confirm.length > 1 && (
                <ul className={style.names}>
                  {confirm.slice(0, 8).map((c) => (
                    <li key={c.id}>{fullName(c)}</li>
                  ))}
                  {confirm.length > 8 && <li>and {confirm.length - 8} more</li>}
                </ul>
              )}
              <Typography variant="body2" color="text.secondary">
                They'll be removed from the customer list on every device. Their proposals, emails
                and photos stay in the CRM. You'll have {UNDO_MS / 1000} seconds to undo.
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setConfirm(null)} autoFocus>
                Cancel
              </Button>
              <Button color="error" variant="contained" onClick={() => confirm && startDelete(confirm)}>
                Delete{confirm && confirm.length > 1 ? ` ${confirm.length}` : ""}
              </Button>
            </DialogActions>
          </Dialog>

          <Snackbar
            open={pending.length > 0}
            message={`Deleted ${pending.length === 1 ? fullName(pending[0]) : `${pending.length} customers`}`}
            action={
              <Button color="inherit" size="small" onClick={undo} className={style.undo}>
                Undo
              </Button>
            }
            anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            className={style.snackbar}
          />
          <Snackbar
            open={!!notice && !pending.length}
            message={notice}
            autoHideDuration={3000}
            onClose={() => setNotice("")}
            anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            className={style.snackbar}
          />
        </>
      }
      mainContent={<CustomerDetailsPage id={id || undefined} />}
    />
  );
}

export default CustomerListPage;
